import React from 'react';

import PropTypes from 'prop-types';

import { FIELDS_TO_CLEAR_AFTER_COUNTRY } from 'components/FormHelper/AddressInput/addressInput.constants';
import { FormField } from 'components/FormHelper/FormField';

import { COUNTRY_OPTIONS } from 'global/constants';
import { evaluateCondition } from 'global/fieldValidationFunction';

import { allowOnlyNumber } from 'utils/FormUtils';

export const FieldInput = ({
  field,
  formik,
  values,
  initialValues,
  comboBoxOptions,
  evaluateConditionOptions,
  disabled = false,
}) => {
  const getLabel = () => {
    const isRequired =
      !field.optional || evaluateCondition(field.optional, evaluateConditionOptions);
    return `${field.fieldName}${isRequired ? '' : '*'}`;
  };

  if (field.name === 'companyCountry') {
    const handleChange = (name, value) => {
      formik.setFieldValue(name, value);
      FIELDS_TO_CLEAR_AFTER_COUNTRY.forEach((countryField) =>
        formik.setFieldValue(countryField, '', false)
      );
    };

    return (
      <FormField
        name={field.name}
        formik={formik}
        label={getLabel()}
        type="comboBox"
        options={COUNTRY_OPTIONS}
        value={values[field.name] ?? ''}
        oldValue={initialValues[field.name]}
        error={Boolean(formik.errors[field.name])}
        helperText={formik.errors[field.name] || ''}
        onChange={handleChange}
        fullWidth
        disabled={disabled}
      />
    );
  }

  // Returns undefined by default so FormField falls back to formik directly:
  // formik.handleChange would silently no-op for date fields, which call
  // onChange(name, value) instead of passing an event.
  const getOnChange = () => {
    if (field.name === 'avaGuestPostal') {
      return (event) => {
        const fixedValue = allowOnlyNumber(event.target.value);
        formik.setFieldValue(field.name, fixedValue);
      };
    }

    return undefined;
  };

  if (field.type === 'comboBox') {
    return (
      <FormField
        name={field.name}
        formik={formik}
        label={field.fieldName}
        type="comboBox"
        options={comboBoxOptions[field.name] || []}
        value={values[field.name]}
        oldValue={initialValues[field.name]}
        error={Boolean(formik.errors[field.name])}
        helperText={formik.errors[field.name] || ''}
        onChange={formik.setFieldValue}
        fullWidth
        disabled={disabled}
      />
    );
  }

  if (field.type === 'select') {
    return (
      <FormField
        name={field.name}
        formik={formik}
        label={getLabel()}
        type="select"
        items={field.items || []}
        value={values[field.name]}
        oldValue={initialValues[field.name]}
        error={Boolean(formik.errors[field.name])}
        helperText={formik.errors[field.name] || ''}
        fullWidth
      />
    );
  }

  return (
    <FormField
      name={field.name}
      formik={formik}
      label={getLabel()}
      infoText={field.infoText}
      oldValue={initialValues[field.name]}
      readOnly={field.readOnly}
      value={values[field.name]}
      onChange={getOnChange()}
      error={Boolean(formik.errors[field.name])}
      helperText={formik.errors[field.name] || ''}
      fullWidth
      disabled={disabled}
    />
  );
};

FieldInput.propTypes = {
  field: PropTypes.object.isRequired,
  formik: PropTypes.object.isRequired,
  values: PropTypes.object.isRequired,
  initialValues: PropTypes.object.isRequired,
  comboBoxOptions: PropTypes.object.isRequired,
  evaluateConditionOptions: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
};

FieldInput.defaultProps = {
  disabled: false,
};
